// BriefBioForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  Box
} from '@mui/material';
import { db, storage } from '../utils/firebaseConfig';
import {
  collection,
  addDoc,
  setDoc,
  doc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import heic2any from 'heic2any';
import 'react-easy-crop/react-easy-crop.css';
import './Bulletin.css';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const invalidFileTypes = ['video/mp4', 'audio/wav', 'image/gif', 'video/x-ms-wmv'];

function checkFileValidity(file, setError) {
  if (invalidFileTypes.includes(file.type)) {
    setError('Invalid file type. MP4, WAV, GIF, and WMV are not allowed.');
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    setError('File size exceeds 5 MB limit.');
    return false;
  }
  setError('');
  return true;
}

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.5);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function convertImageToJPG(file) {
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    return heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  }
  return compressImage(file);
}

function createImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}

async function getCroppedImg(src, pixelCrop) {
  const img = await createImage(src);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const maxSize = Math.max(img.width, img.height);
  const safe = 2 * ((maxSize / 2) * Math.sqrt(2));
  canvas.width = canvas.height = safe;
  ctx.translate(safe / 2, safe / 2);
  ctx.translate(-safe / 2, -safe / 2);
  ctx.drawImage(img, safe / 2 - img.width * 0.5, safe / 2 - img.height * 0.5);
  const data = ctx.getImageData(0, 0, safe, safe);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(
    data,
    0 - safe / 2 + img.width * 0.5 - pixelCrop.x,
    0 - safe / 2 + img.height * 0.5 - pixelCrop.y
  );
  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg'));
}

// ✅ Sentence splitting function matching BriefBio.js exactly
const ABBR = [
  'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'sr.', 'jr.',
  'inc.', 'ltd.', 'co.', 'vs.', 'etc.', 'e.g.', 'i.e.'
];

const isAbbreviation = (word) => {
  const cleaned = word.toLowerCase().replace(/[^a-z.]/g, '');
  return ABBR.includes(cleaned);
};

const splitText = (text = '') => {
  if (!text.trim()) return [];

  const words = text.trim().split(/\s+/);
  const sentences = [];
  let currentSentence = [];

  words.forEach((word, idx) => {
    currentSentence.push(word);
    const isLastWord = idx === words.length - 1;
    const endsWithPunctuation = /[.!?]$/.test(word);

    if ((endsWithPunctuation && !isAbbreviation(word)) || isLastWord) {
      sentences.push(currentSentence.join(' '));
      currentSentence = [];
    }
  });

  return sentences;
};

const BriefBioForm = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [source1, setSource1] = useState('');
  const [link1, setLink1] = useState('');
  const [source2, setSource2] = useState('');
  const [link2, setLink2] = useState('');
  const [source3, setSource3] = useState('');
  const [link3, setLink3] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [success, setSuccess] = useState(false);
  const [editingBio, setEditingBio] = useState(null);

  const [imageBlob, setImageBlob] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const safeString = (val) => (typeof val === 'string' ? val : '');

  const processSources = (sources) => {
    const processed = Array(3).fill({ name: '', link: '' });
    if (!Array.isArray(sources)) return processed;
    return processed.map((_, i) => ({
      name: safeString(sources[i]?.name),
      link: safeString(sources[i]?.link)
    }));
  };

  const updateWordCount = (text) => {
    const words = text.split(/\s+/).filter(Boolean);
    setWordCount(words.length);
  };

  const onCropComplete = useCallback((_, p) => setCroppedAreaPixels(p), []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (files) => {
      const file = files[0];
      if (!file) return;
      if (!checkFileValidity(file, setUploadError)) return;
      setIsCropping(true);
      try {
        const blob = await convertImageToJPG(file);
        setSelectedImage(URL.createObjectURL(blob));
        setImageBlob(blob);
        setCroppedPreview(null);
      } catch (err) {
        console.error(err);
        setUploadError('Error processing image.');
      }
    }
  });

  const saveCrop = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(selectedImage, croppedAreaPixels);
      setCroppedPreview(URL.createObjectURL(blob));
      setSelectedImage(null);
      setImageBlob(blob);
      setIsCropping(false);
    } catch (err) {
      console.error(err);
      setUploadError('Error cropping image.');
    }
  };

  const resetImage = () => {
    setSelectedImage(null);
    setCroppedPreview(null);
    setImageBlob(null);
    setIsCropping(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    const type = params.get('type');
    if (!editId || type !== 'briefBio') return;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'briefBios', editId));
        if (!snap.exists()) throw new Error('Bio not found');
        const data = snap.data();

        setName(safeString(data.name));
        setBio(safeString(data.bio));
        updateWordCount(data.bio || '');
        const sources = processSources(data.sources);
        setSource1(sources[0].name); setLink1(sources[0].link);
        setSource2(sources[1].name); setLink2(sources[1].link);
        setSource3(sources[2].name); setLink3(sources[2].link);
        setCroppedPreview(data.imageUrl);
        setEditingBio({ id: snap.id, ...data });
      } catch (err) {
        console.error(err);
        alert('Failed to load bio.');
        navigate('/briefbio', { replace: true });
      }
    })();
  }, [location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !bio.trim() || (!imageBlob && !croppedPreview)) {
      alert('Please complete all fields and upload an image.');
      return;
    }
    if (wordCount > 80) {
      alert('Your bio exceeds the 80-word limit.');
      return;
    }

    setSubmitting(true);
    setUploadError('');
    setSuccess(false);

    try {
      let imageUrl = croppedPreview;
      if (imageBlob) {
        const sRef = ref(storage, `briefBios/${Date.now()}_bio.jpg`);
        await uploadBytes(sRef, imageBlob);
        imageUrl = await getDownloadURL(sRef);
      }

      // ✅ FIXED: Correct the sources array with proper function calls
      const sources = [
        { name: safeString, link: safeString(link1) },
        { name: safeString, link: safeString(link2) },
        { name: safeString, link: safeString(link3) }
      ].filter(s => s.name || s.link);

      const data = {
        name: name.trim(),
        bio: bio.trim(),
        imageUrl,
        sources,
        updatedAt: serverTimestamp(),
        createdBy: currentUser?.uid || ''
      };

      // ✅ FIXED: Declare newDocRef outside the if/else block
      let newDocRef;

      if (editingBio?.id) {
        await setDoc(doc(db, 'briefBios', editingBio.id), {
          ...data,
          createdAt: editingBio.createdAt,
          lastEdited: serverTimestamp()
        });
      } else {
        // ✅ FIXED: Assign (not declare) newDocRef here
        newDocRef = await addDoc(collection(db, 'briefBios'), {
          ...data,
          createdAt: serverTimestamp()
        });
      }

      resetImage();
      setName(''); setBio(''); setWordCount(0);
      setSource1(''); setLink1('');
      setSource2(''); setLink2('');
      setSource3(''); setLink3('');
      setSuccess(true);
      
      // ✅ FIXED: Now newDocRef is accessible here
      setTimeout(() => {
        if (editingBio?.id) {
          navigate(`/briefbios/${editingBio.id}`);
        } else {
          navigate(`/briefbios/${newDocRef.id}`);
        }
      }, 1500);
      
    } catch (err) {
      console.error(err);
      setUploadError('Error saving bio.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 12, p: 3, border: '1px solid #ccc', borderRadius: '12px', backgroundColor: '#f9f9f9' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {editingBio ? 'Edit Public Figure Bio' : 'Add a Public Figure Bio'}
        </Typography>

        {!selectedImage && !croppedPreview && (
          <div {...getRootProps()} className="face-dropzone">
            <input {...getInputProps()} />
            <p>Click or Drag/Drop to Upload</p>
          </div>
        )}

        {selectedImage && !croppedPreview && (
          <div className="face-cropper-container">
            <div className="cropper-wrapper">
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={false}
              />
            </div>
            <div className="cropper-controls">
              <button type="button" onClick={saveCrop} className="btn-save">Save</button>
              <button type="button" onClick={resetImage} className="btn-cancel">Cancel</button>
            </div>
          </div>
        )}

        {croppedPreview && (
          <div className="face-preview-container">
            <img src={croppedPreview} alt="preview" style={{ maxWidth: 200, borderRadius: 4 }} />
            <button type="button" onClick={resetImage} className="btn-remove">✕</button>
          </div>
        )}

        {uploadError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>{uploadError}</Typography>
        )}

        {/* ✅ UPDATED: Live Preview Section - Fixed width to match form */}
        <div style={{ 
          maxWidth: 600, 
          margin: '24px auto 16px auto',
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#fff'
        }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Live Preview</Typography>
          
          {name && (
            <h2 style={{ textAlign: 'center', margin: '0 0 20px 0' }}>
              {name}
            </h2>
          )}

          {croppedPreview && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <div style={{ 
                  width: 260, 
                  height: 260,
                  border: '3px solid #ccc',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={croppedPreview}
                    alt={name || 'Preview'}
                    style={{ 
                      objectFit: 'cover', 
                      width: '100%', 
                      height: '100%',
                      display: 'block'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {bio && (
            <div style={{ marginTop: 20, fontSize: 16, lineHeight: 1.6, color: '#333', textAlign: 'left' }}>
              {splitText(bio).length > 1 ? (
                <ul style={{ listStyleType: 'disc', marginLeft: '20px', paddingInlineStart: '20px' }}>
                  {splitText(bio).map((sentence, idx) => (
                    <li key={idx} style={{ textAlign: 'left' }}>{sentence}</li>
                  ))}
                </ul>
              ) : (
                <p>{bio}</p>
              )}
            </div>
          )}

          {(source1 || source2 || source3) && (
            <div style={{ marginTop: 15 }}>
              <h4 style={{ marginBottom: 6, fontSize: 15 }}>Sources:</h4>
              <ul style={{ paddingLeft: 18, color: '#1a0dab', fontSize: 15 }}>
                {[{ name: source1, link: link1 }, { name: source2, link: link2 }, { name: source3, link: link3 }].map((src, i) => (
                  src.name && src.link ? (
                    <li key={i}>
                      <a
                        href={src.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1a0dab', textDecoration: 'underline' }}
                      >
                        {src.name}
                      </a>
                    </li>
                  ) : null
                ))}
              </ul>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: '0 auto' }}>
          <TextField fullWidth label="Full Name" value={name} onChange={e => setName(e.target.value)} sx={{ mt: 2 }} />

          <TextField
            fullWidth
            label="80-Word Bio"
            multiline
            rows={3}
            value={bio}
            onChange={(e) => {
              setBio(e.target.value);
              updateWordCount(e.target.value);
            }}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" sx={{ color: wordCount > 80 ? 'red' : 'gray', display: 'block', mt: 1 }}>
            Word Count: {wordCount} / 80
          </Typography>

          <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>Sources (optional):</Typography>

          <TextField fullWidth label="Source #1" value={source1} onChange={e => setSource1(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth label="Link #1" value={link1} onChange={e => setLink1(e.target.value)} sx={{ mt: 2 }} />

          <TextField fullWidth label="Source #2" value={source2} onChange={e => setSource2(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth label="Link #2" value={link2} onChange={e => setLink2(e.target.value)} sx={{ mt: 2 }} />

          <TextField fullWidth label="Source #3" value={source3} onChange={e => setSource3(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth label="Link #3" value={link3} onChange={e => setLink3(e.target.value)} sx={{ mt: 2 }} />

          <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={submitting}>
            {submitting ? (<><CircularProgress size={20} sx={{ mr: 1 }} />Submitting...</>) : (editingBio ? 'Update Bio' : 'Submit Bio')}
          </Button>

          {success && (
            <Typography color="success.main" variant="body2" sx={{ mt: 2 }}>
              {editingBio ? 'Bio successfully updated!' : 'Bio successfully added!'}
            </Typography>
          )}
        </form>
      </Box>
    </Container>
  );
};

export default BriefBioForm;